import React, { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { assetsSavedSearchRetrieve } from '@goldcast/api/content';
import SessionDetailLayout from '../SessionDetail/SessionDetailLayout';
import { NavItemType } from '../SessionDetail/components/NavItem';
import { clearSavedAISearch, setSavedAISearch } from '@/stores/savedAISearch';
import { useAppContext } from '@/context/AppContext/AppContext';
import { showErrorToast } from '@/libs/toast/toast';
import { unseenClipIds } from '@/stores/unseenClips';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';

export default function SavedSearchDetailLayout({ isLoading }: { isLoading: boolean }) {
  const unseenClipsStore = useSyncExternalStore(unseenClipIds.subscribe, unseenClipIds.getSnapshot);
  const { id } = useParams<{ id: string }>();
  const { logger } = useAppContext();
  const navigate = useNavigate();

  useEffect(() => {
    assetsSavedSearchRetrieve({ id: id! })
      .then(res => setSavedAISearch(res))
      .catch(err => {
        logger.error(`Error loading Saved Search with id ${id}. Stack trace: ${err}`);
        showErrorToast(`Whoops! We hit a snag while fetching the saved search. Please try again.`);
        navigate(-1);
      });

    return () => {
      clearSavedAISearch();
    };
  }, [id]);

  const onClickClipHandler = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    EventBus.dispatch(CustomEvents.ClipListPopUp, { isOpen: true });
  }, []);

  const nav: NavItemType[] = useMemo(() => {
    return [
      {
        iconName: 'IconPlayerPlay',
        filledIconName: 'IconPlayerPlayFilled',
        label: 'Clips',
        href: `/search/${id}/clips`,
        unseenCount: unseenClipsStore.length,
        visible: true,
        onClick: onClickClipHandler
      },
      {
        iconName: 'IconThumbUp',
        filledIconName: 'IconThumbUpFilled',
        label: 'Social',
        href: `/search/${id}/social`,
        visible: true
      },
      {
        iconName: 'IconArticle',
        filledIconName: 'IconArticleFilled',
        label: 'Blog',
        href: `/search/${id}/blog`,
        visible: true
      }
    ];
  }, [id, unseenClipsStore.length]);

  return <SessionDetailLayout isLoading={isLoading} navMenu={nav} />;
}
